import {
  Component,
  ElementRef,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NotificationsService } from "angular2-notifications";
import { debounceTime, distinctUntilChanged, Subject } from "rxjs";
import { NewProduct, ProductCustomer } from "src/app/main/model/ddb.model";
import { HasId } from "src/app/main/model/generics";
import {
  IQueryFilter,
  QueryResult,
} from "src/app/main/model/query.filter.class";
import { UnleashedCustomer } from "src/app/main/model/unleashed.model";
import { CustomerProductService } from "src/app/main/services/customerProduct.service";
import { PaginationStateService } from "src/app/main/services/pagination.state.service";
import { ModalComponent } from "src/app/main/template/model.component";
import { BackorderProductsEmailPreviewComponent } from "../backorder-products-email-preview/backorder-products-email-preview.component";
import { ProductService } from "src/app/main/services/product.service";
import { Utils } from "src/app/main/util/utils";
import { SessionApi } from "src/app/main/api/session.api";

type ISortOption = {
  id: number;
  text: string;
  field: string;
};

type ISortOptions = ISortOption[];

@Component({
  selector: "app-backorder-products",
  templateUrl: "./backorder-products.component.html",
  styleUrls: ["./backorder-products.component.sass"],
})
export class BackorderProductsComponent implements OnInit {
  @Input() customer!: UnleashedCustomer;
  customerId!: number;
  isAlreadyChecked: boolean = false;

  public totalCountActive: number = 0;
  public currentPageActive: number = 1;
  queryResult: QueryResult<ProductCustomer & HasId<string>> = new QueryResult();
  query: IQueryFilter = new IQueryFilter({
    sortBy: "id",
    order: "desc",
    limit: 50,
    include: [
      {
        association: "product",
        required: true,
      },
    ],
  });

  sortOptions: ISortOptions = [
    {
      id: 1,
      text: "Custom Price",
      field: "customPrice",
    },
    {
      id: 2,
      text: "Id",
      field: "id",
    },
  ];

  filterType: {
    type: string;
    showDropDown: boolean;
    dropDownList?: { name: string; id: string }[];
  }[] = [
    {
      type: "Status",
      showDropDown: true,
      dropDownList: [
        { id: "NO_SUBSTITUTE", name: "No Substitute" },
        { id: "APPROVAL_PENDING", name: "Approval Pending" },
        { id: "APPROVED", name: "Approved" },
        { id: "DECLINED", name: "Declined" },
      ],
    },
    {
      type: "Substitute Rule",
      showDropDown: true,
      dropDownList: [
        {
          id: "REQUIRE_APPROVAL_PER_ORDER",
          name: "Require Approval Per Order",
        },
        {
          id: "AUTO_SWAP",
          name: "Auto Swap",
        },
      ],
    },
  ];

  private searchTerms: Subject<string> = new Subject<string>();
  paginationKey = "customer-backorder-products";

  substitutePayload!: {
    id: number;
    substituteProductId: number | null;
    substituteRule: string | null;
    note: string;
    status: string;
  };

  customerPorductOptions: {
    id: number | undefined;
    text: string;
    product: NewProduct;
  }[] = [];
  private customerSearchTerms: Subject<string> = new Subject<string>();
  public customerProductQuery = new IQueryFilter({
    limit: 10,
    sortBy: "name",
    order: "desc",
  });

  public noItemsFoundText!: string;
  selectedProduct!: NewProduct;
  selectedSubstituteProduct: NewProduct | null = null;
  modalType: "SEND" | "OVERRIDE" = "SEND";
  selectedProductIds: Set<number> = new Set<number>();

  @ViewChild("emailTemplate", { read: ElementRef })
  emailTemplate!: ElementRef;
  selectedProductPrice: number = 0;
  @Input() inCustomer: boolean = false;

  constructor(
    private productCustomerService: CustomerProductService,
    private paginationState: PaginationStateService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private notifications: NotificationsService,
    private productsService: ProductService,
    public utils: Utils,
    public session: SessionApi,
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.customerId =  this.inCustomer ? parseInt(params.customerId) : this.session.$customerData.getValue()?.id || 0  ;
      this.loadCustomerProducts();
      this.search();
      this.customerProductSearch();
    });
  }

  openSendSubstituteApprovalRequestModal(
    content: TemplateRef<any>,
    item: ProductCustomer & HasId<string>,
    type: "SEND" | "OVERRIDE",
  ) {
    this.modalType = type;

    if (item) {
      this.substitutePayload = {
        id: item.id!,
        substituteProductId: item.substituteProductId || null,
        substituteRule: item.substituteRule || null,
        note: item.note || "",
        status:
          item.status == "NO_SUBSTITUTE"
            ? "APPROVAL_PENDING"
            : item.status || "NO_SUBSTITUTE",
      };
      this.selectedProduct = item.product;
      this.selectedProductPrice = item.customPrice
        ? item.customPrice
        : item.product.price || 0;
    }

    this.customerProductQuery.limit = 10;
    this.customerSearchTerms.next("");
    if (item.substituteProductId) {
      this.getCustomerProduct(false, undefined, item);
    }

    if (item.substituteProduct) {
      this.selectedSubstituteProduct = item.substituteProduct;
    } else {
      this.selectedSubstituteProduct = null;
    }

    this.modalService.open(content, {
      size: "lg",
      centered: true,
      windowClass: "bulk-order",
    });
  }

  onSubmitSubstituteRequest() {
    if (this.selectedProduct.id === this.selectedSubstituteProduct?.id) {
      this.notifications.warn(
        "Validation Error",
        "Original and Substitute product cannot be the same.",
      );
      return;
    }
    if (
      !this.substitutePayload.substituteProductId ||
      !this.substitutePayload.substituteRule
    ) {
      this.notifications.warn(
        "Validation Error",
        "Please select both substitute product and rule",
      );
      return;
    }

    if (this.modalType === "OVERRIDE" && !this.substitutePayload.note) {
      this.notifications.warn("Validation Error", "Please Enter a note");
      return;
    }

    if (this.modalType === "OVERRIDE") {
      this.substitutePayload.status = "APPROVED";
    }

    this.productCustomerService
      .submitSubstituteRequest(this.substitutePayload)
      .subscribe(() => {
        this.modalService.dismissAll();
        this.loadCustomerProducts();
      });
  }

  public openNoSubstituteModal(id: number) {
    const modal = this.modalService.open(ModalComponent, {
      scrollable: false,
      size: "sm",
      centered: true,
      backdrop: true,
      windowClass: "deleteModal",
    });

    if (modal.componentInstance) {
      const component = modal.componentInstance as ModalComponent;

      component.title = "No Substitute";
      component.showIcon = false;
      component.data = `  
            <div>
              <h4 class="title">No Substitute</h4>
              <p class="desc">Are you sure you want to remove the substitute product?</p>
            </div>`;
      component.buttons = [
        {
          text: "Yes",
          action: "close",
          value: true,
          class: "btn-success",
        },
        {
          text: "Cancel",
          action: "close",
          value: false,
          class: "btn btn-secondary",
        },
      ];
    }

    modal.result
      .then((isMarkAsNoSubstitute) => {
        if (isMarkAsNoSubstitute && id) {
          this.markAsNotSubstitute(id);
        }
      })
      .catch(() => {
        modal.dismiss();
      });
  }

  markAsNotSubstitute(id: number) {
    this.productCustomerService
      .submitSubstituteRequest({
        id,
        substituteProductId: null,
        substituteRule: "REQUIRE_APPROVAL_PER_ORDER",
        note: "",
        status: "NO_SUBSTITUTE",
      })
      .subscribe(() => {
        this.loadCustomerProducts();
        this.modalService.dismissAll();
      });
  }

  getCustomerProduct(
    isScroll: boolean = false,
    data?: {
      id: number;
      text: string;
      product: NewProduct;
    },
    item?: ProductCustomer & HasId<string>,
  ) {
    this.noItemsFoundText = "Fetching...";
    // @ts-ignore
    this.customerProductQuery.customerId = this.customerId;
    // @ts-ignore
    this.customerProductQuery.isBackOrderList = true;
    if (this.customer.substituteProductsRange === "all_products") {
      // @ts-ignore
      this.customerProductQuery.isPublicProducts = true;
    }

    if (isScroll) {
      this.customerProductQuery.skip += 10;
    } else {
      this.customerProductQuery.skip = 0;
      this.customerPorductOptions = [];
    }

    this.productsService
      .list(this.customerProductQuery, {}, false)
      .subscribe((res) => {
        if (res.rows.length === 0) {
          this.noItemsFoundText = "Product not found";
        }

        const products = res.rows.map((data) => ({
          id: data.id,
          text: data.code + "-" + data.name,
          product: data,
        }));

        this.customerPorductOptions = this.customerPorductOptions
          ? [...this.customerPorductOptions, ...products]
          : products;

        if (
          item?.substituteProduct &&
          !this.customerPorductOptions.some(
            (p) => p.id === item.substituteProduct!.id,
          )
        ) {
          this.customerPorductOptions.push({
            id: item.substituteProduct.id,
            text:
              item.substituteProduct.code + "-" + item.substituteProduct.name,
            product: item.substituteProduct,
          });
        }
      });
  }

  onCustomerProductSearch(searchTerm: { term: string }) {
    this.customerSearchTerms.next(searchTerm.term);
  }

  onCustomerProductChange(item: NewProduct) {
    this.selectedSubstituteProduct = item;
  }

  getSelectedSubstitutePriceComparison(): string {
    const substitutePrice = this.selectedSubstituteProduct?.price || 0;
    const difference = substitutePrice - this.selectedProductPrice;

    if (difference < 0) {
      return `Save $${this.utils.twoDecimalPlacesString(Math.abs(difference))}`;
    }

    if (difference > 0) {
      return `+$${this.utils.twoDecimalPlacesString(difference)} per item`;
    }

    return "No price change";
  }

  customerProductSearch() {
    this.customerSearchTerms
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        if (searchTerm && searchTerm.length)
          this.customerProductQuery.filter["$or"] = {
            name: { $like: "%" + searchTerm + "%" },
            code: { $like: "%" + searchTerm + "%" },
            uri: { $like: "%" + searchTerm + "%" },
            description: { $like: "%" + searchTerm + "%" },
          };
        else delete this.customerProductQuery.filter["$or"];
        this.getCustomerProduct();
      });
  }

  loadCustomerProducts() {
    this.query.skip =
      this.currentPageActive * this.query.limit - this.query.limit;
    this.query.filter["customerId"] = this.customerId;

    this.productCustomerService.list(this.query).subscribe((queryResult) => {
      this.queryResult = queryResult;
      this.totalCountActive = queryResult.count;
      if (
        this.totalCountActive > 0 &&
        queryResult.rows.length == 0 &&
        !this.isAlreadyChecked
      ) {
        this.isAlreadyChecked = true;
        this.query.skip = 0;
        this.currentPageActive = 1;
        this.loadCustomerProducts();
      }
    });
  }

  search() {
    this.searchTerms
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        if (this.query.include) {
          if (searchTerm && searchTerm.length) {
            this.query.include[0].filter = this.query.include[0].filter || {};
            this.query.include[0].filter["$or"] = {
              name: { $like: "%" + searchTerm + "%" },
              code: { $like: "%" + searchTerm + "%" },
            };
          } else {
            delete this.query.include[0].filter;
          }
        }
        this.loadCustomerProducts();
      });
  }

  updateSearchTerm(searchTerm: string): void {
    this.currentPageActive = 1;
    this.searchTerms.next(searchTerm);
  }

  updateSortField(sort: string) {
    this.query.sortBy = sort;
    this.loadCustomerProducts();
  }

  /**
   * @description UI helper method for retrieving the text of the selected sort option
   * @returns {string | undefined}
   */
  getSortText = (): string | undefined => {
    if (!this.query.sortBy) return undefined;

    const sortOption = this.sortOptions.find(
      (option) => option.field === this.query.sortBy,
    );

    if (sortOption) return sortOption.text;
    return undefined;
  };

  pageChangedActive(page: number) {
    this.currentPageActive = page;
    this.paginationState.saveState(
      this.paginationKey,
      this.currentPageActive,
      this.query.limit,
    );

    this.loadCustomerProducts();
  }

  /**
   * @description Ensures the page number is in sync across multiple pagination components
   *
   * @param {number} pageSize Broadcast pageSize value
   */
  pageSizeChanged(pageSize: number): void {
    this.query.limit = pageSize;
    this.currentPageActive = 1; // Reset to the first page when page size changes
    this.loadCustomerProducts();
  }

  toggleSelectProductId(item: ProductCustomer): void {
    if (this.selectedProductIds.has(item.id!)) {
      this.selectedProductIds.delete(item.id!);
    } else {
      this.selectedProductIds.add(item.id!);
    }
  }

  isProductIdSelected(id: number): boolean {
    return this.selectedProductIds.has(id);
  }

  toggleSelectAllProductIds(): void {
    const rows = this.queryResult.rows;

    if (this.isAllProductIdsSelected()) {
      this.selectedProductIds.clear();
      return;
    }

    rows.forEach((item) => {
      this.selectedProductIds.add(item.id);
    });
  }

  isAllProductIdsSelected(): boolean {
    if (!this.queryResult.rows.length) return false;

    return this.queryResult.rows.every((item) =>
      this.selectedProductIds.has(item.id),
    );
  }

  get selectedItems(): ProductCustomer[] {
    return this.queryResult.rows.filter((row) =>
      this.selectedProductIds.has(row.id),
    );
  }

  hasValidStatusForBulk(): boolean {
    const items = this.selectedItems;
    if (!items.length) return false;

    return items.every((i) => i.status !== "NO_SUBSTITUTE");
  }

  canChangeRuleToApproval(): boolean {
    const items = this.selectedItems;
    if (!items.length) return false;

    return items.every((i) => i.substituteRule === "AUTO_SWAP");
  }

  canSendBulkApprovalRequest(): boolean {
    const items = this.selectedItems;
    if (!items.length) return false;

    return items.every((i) => i.status === "APPROVAL_PENDING");
  }

  getSendBulkErrorText(): string {
    if (!this.canSendBulkApprovalRequest()) {
      return "Selected Products Must Have Approval Pending Status";
    } else if (!this.customer.enableSubstituteApproval) {
      return "Turn On Backorder Email Notification";
    }

    return "";
  }

  onSearch(event: { type: string; value: string }) {
    const { type, value } = event;

    if (type === "Status") {
      if (value) {
        this.query.filter.status = value;
      } else {
        delete this.query.filter.status;
      }
    }

    if (type === "Substitute Rule") {
      if (value) {
        this.query.filter.substituteRule = value;
      } else {
        delete this.query.filter.substituteRule;
      }
    }

    this.currentPageActive = 1;
    this.loadCustomerProducts();
  }

  openBulkConfirmModal(title: string, message: string, onConfirm: () => void) {
    const modal = this.modalService.open(ModalComponent, {
      scrollable: false,
      size: "sm",
      centered: true,
      backdrop: true,
      windowClass: "deleteModal",
    });

    const component = modal.componentInstance as ModalComponent;

    component.title = title;
    component.showIcon = false;
    component.data = `
    <div>
      <h4 class="title">${title}</h4>
      <p class="desc">${message}</p>
    </div>
  `;

    component.buttons = [
      {
        text: "Yes",
        action: "close",
        value: true,
        class: "btn-success",
      },
      {
        text: "Cancel",
        action: "close",
        value: false,
        class: "btn btn-secondary",
      },
    ];

    modal.result
      .then((confirmed) => {
        if (confirmed) onConfirm();
      })
      .catch(() => modal.dismiss());
  }

  bulkMarkAsNoSubstitute(): void {
    if (!this.hasValidStatusForBulk()) return;

    this.openBulkConfirmModal(
      "Mark as No Substitute",
      "Are you sure you want to mark selected products as 'No Substitute'?",
      () => {
        const payload = this.selectedItems.map((item) => ({
          id: item.id!,
          status: "NO_SUBSTITUTE",
          substituteProductId: null,
          substituteRule: "REQUIRE_APPROVAL_PER_ORDER",
        }));

        this.productCustomerService
          .updateBulkSubstituteproduct(payload)
          .subscribe(() => {
            this.selectedProductIds.clear();
            this.loadCustomerProducts();
          });
      },
    );
  }

  bulkApplyApprovalRule(): void {
    if (!this.selectedItems.length && !this.canChangeRuleToApproval()) return;

    this.openBulkConfirmModal(
      "Apply Rule",
      "Are you sure you want to apply 'Require Approval Per Order' to selected products?",
      () => {
        const payload = this.selectedItems.map((item) => ({
          id: item.id!,
          substituteRule: "REQUIRE_APPROVAL_PER_ORDER",
        }));

        this.productCustomerService
          .updateBulkSubstituteproduct(payload)
          .subscribe(() => {
            this.selectedProductIds.clear();
            this.loadCustomerProducts();
          });
      },
    );
  }

  openEmailPreview() {
    if (
      !this.canSendBulkApprovalRequest() ||
      !this.customer.enableSubstituteApproval
    ) {
      return;
    }

    const modalRef = this.modalService.open(
      BackorderProductsEmailPreviewComponent,
      {
        size: "lg",
        centered: true,
        // scrollable: true,
        windowClass: "email-preview-modal",
      },
    );

    modalRef.componentInstance.products = this.selectedItems;
    modalRef.componentInstance.email = this.customer.backorderUser?.email;

    modalRef.componentInstance.approvalSent.subscribe(() => {
      this.selectedProductIds.clear();
      this.loadCustomerProducts();
      modalRef.close();
    });
  }

  getRuleText(rule: string) {
    switch (rule) {
      case "AUTO_SWAP":
        return "Auto Swap";

      case "REQUIRE_APPROVAL_PER_ORDER":
        return "Require Approval Per Order";

      default:
        return "";
    }
  }

  fetchColor(status: string) {
    switch (status) {
      case "NO_SUBSTITUTE":
        return "orange";
      case "APPROVAL_PENDING":
        return "purple";
      case "APPROVED":
        return "dark-blue";
      case "DECLINED":
        return "red";
      default:
        return "gray";
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case "NO_SUBSTITUTE":
        return "No Substitute";
      case "APPROVAL_PENDING":
        return "Approval Pending";
      case "APPROVED":
        return "Approved";
      case "DECLINED":
        return "Declined";
      default:
        return status || "N/A";
    }
  }

  generateSubstituteReport(type: "All" | "Selected"): void {
    if (type === "Selected" && !this.hasValidStatusForBulk()) {
      return;
    }

    this.openBulkConfirmModal(
      "Generate Substitute Report",
      "Do you want to generate and download the substitute report?",
      () => {
        const payload: {
          customerId: string;
          id?: string[];
        } = {
          customerId: this.customerId.toString(),
        };

        const selectedIds = Array.from(this.selectedProductIds).map(String);

        if (selectedIds.length > 0 && type === "Selected") {
          payload.id = selectedIds;
        }

        this.productCustomerService
          .generateSubstituteReport(payload)
          .subscribe((res) => {
            if (res?.s3url) {
              this.downloadFile(res.s3url);
            }
          });
      },
    );
  }

  private downloadFile(url: string): void {
    const link = document.createElement("a");
    link.href = url;
    link.rel = "noopener noreferrer";
    link.download = "SubstituteReport.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
